import { DotsThree, Files, GitBranch, Plus, Users } from "@phosphor-icons/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { Project, ProjectStatus } from "~/api/governanceTypes";
import {
  useArchiveProject,
  useDeleteProject,
  useGroups,
  useProjectAbilities,
  useProjects,
  useRestoreProject,
} from "~/api/queries";
import { ABILITY } from "~/api/types";
import { Pill, Row, Rows } from "~/features/automation/components";
import { ProjectDialog, projectStatusLabel } from "~/features/projects/ProjectDialog";
import { ProjectPeopleDialog } from "~/features/projects/ProjectPeopleDialog";
import { ProjectRepositoriesDialog } from "~/features/projects/ProjectRepositoriesDialog";
import { Select } from "~/features/settings/controls";
import { Button } from "~/foundation/components/Button";
import { ConfirmDialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Menu, type MenuItem } from "~/foundation/components/Menu";
import { Spinner } from "~/foundation/components/Spinner";
import { formatRelativeTime } from "~/foundation/utils/time";
import { usePolicy } from "~/policies/usePolicy";
import { useShellStore } from "~/shell/store";

const STATUS_TONE: Record<ProjectStatus, "neutral" | "accent" | "success" | "warning" | "danger"> =
  {
    active: "accent",
    onHold: "warning",
    completed: "success",
    cancelled: "neutral",
    archived: "neutral",
  };

function ProjectRow({ project, groupName }: { project: Project; groupName: string | undefined }) {
  const { t, i18n } = useTranslation();
  const can = usePolicy(project.id);
  const archiveProject = useArchiveProject();
  const restoreProject = useRestoreProject();
  const deleteProject = useDeleteProject();
  const [editing, setEditing] = useState(false);
  const [managingPeople, setManagingPeople] = useState(false);
  const [managingRepositories, setManagingRepositories] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const items: MenuItem[] = [
    ...(can[ABILITY.projectManagePermissions]
      ? [
          {
            type: "button" as const,
            title: t("People…"),
            icon: Users,
            onClick: () => setManagingPeople(true),
          },
        ]
      : []),
    ...(can[ABILITY.projectUpdate]
      ? [
          { type: "button" as const, title: t("Edit…"), onClick: () => setEditing(true) },
          {
            type: "button" as const,
            title: t("Repositories…"),
            icon: GitBranch,
            onClick: () => setManagingRepositories(true),
          },
        ]
      : []),
    { type: "separator" as const },
    ...(project.status === "archived"
      ? [
          {
            type: "button" as const,
            title: t("Restore"),
            onClick: () =>
              restoreProject.mutate(project.id, {
                onSuccess: () => toast.success(t("Project restored")),
              }),
          },
        ]
      : [
          {
            type: "button" as const,
            title: t("Archive"),
            onClick: () =>
              archiveProject.mutate(project.id, {
                onSuccess: () => toast.success(t("Project archived")),
              }),
          },
        ]),
    ...(can[ABILITY.projectDelete]
      ? [
          {
            type: "button" as const,
            title: t("Delete…"),
            dangerous: true,
            onClick: () => setConfirmDelete(true),
          },
        ]
      : []),
  ];

  return (
    <Row>
      {project.icon ? (
        <span aria-hidden className="w-5 shrink-0 text-center text-body leading-none">
          {project.icon}
        </span>
      ) : (
        <Files size={18} aria-hidden className="shrink-0 text-text-tertiary" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-body text-text">{project.name}</p>
        {project.description ? (
          <p className="truncate text-small text-text-tertiary">{project.description}</p>
        ) : null}
      </div>
      <span className="hidden w-32 truncate text-small text-text-secondary sm:inline">
        {groupName ?? t("No team")}
      </span>
      <Pill tone={STATUS_TONE[project.status]}>{projectStatusLabel(project.status, t)}</Pill>
      <span className="hidden w-24 text-small text-text-tertiary sm:inline">
        {formatRelativeTime(project.updatedAt, i18n.language)}
      </span>
      <Menu items={items} trigger={<IconButton icon={DotsThree} aria-label={t("Actions")} />} />

      {editing ? <ProjectDialog project={project} onClose={() => setEditing(false)} /> : null}
      {managingPeople ? (
        <ProjectPeopleDialog project={project} onClose={() => setManagingPeople(false)} />
      ) : null}
      {managingRepositories ? (
        <ProjectRepositoriesDialog
          project={project}
          onClose={() => setManagingRepositories(false)}
        />
      ) : null}
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("Delete project?")}
        description={project.name}
        confirmLabel={t("Delete")}
        cancelLabel={t("Cancel")}
        closeLabel={t("Close")}
        dangerous
        busy={deleteProject.isPending}
        onConfirm={() =>
          deleteProject.mutate(project.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              toast.success(t("Project deleted"));
            },
          })
        }
      />
    </Row>
  );
}

export function ProjectsSection() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<ProjectStatus | "">("");
  const [groupId, setGroupId] = useState("");
  const { data: projects, isLoading } = useProjects({
    ...(status ? { status } : {}),
    ...(groupId ? { groupId } : {}),
  });
  const { data: groups } = useGroups();
  const ids = useMemo(() => (projects ?? []).map((p) => p.id), [projects]);
  useProjectAbilities(ids);

  const groupName = (id: string | null) => groups?.find((g) => g.id === id)?.name;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <Select
            value={status}
            onChange={(event) => setStatus(event.target.value as ProjectStatus | "")}
          >
            <option value="">{t("All statuses")}</option>
            <option value="active">{projectStatusLabel("active", t)}</option>
            <option value="onHold">{projectStatusLabel("onHold", t)}</option>
            <option value="completed">{projectStatusLabel("completed", t)}</option>
            <option value="archived">{projectStatusLabel("archived", t)}</option>
            <option value="cancelled">{projectStatusLabel("cancelled", t)}</option>
          </Select>
          <Select value={groupId} onChange={(event) => setGroupId(event.target.value)}>
            <option value="">{t("All teams")}</option>
            {(groups ?? []).map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </Select>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => useShellStore.getState().openDialog({ kind: "newProject" })}
        >
          {t("New project")}
        </Button>
      </div>

      {isLoading ? (
        <Spinner label={t("Loading")} />
      ) : !projects?.length ? (
        <Empty icon={Files} message={t("No projects yet")} />
      ) : (
        <Rows>
          {projects.map((project) => (
            <ProjectRow key={project.id} project={project} groupName={groupName(project.groupId)} />
          ))}
        </Rows>
      )}
    </div>
  );
}
